import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Ticket } from 'lucide-react';
import { getApiBaseUrl } from '../lib/utils';
import { track } from '../lib/analytics';
import { useAuth } from '../context/AuthContext';
import CondoLayout from './CondoLayout';
import TicketCard from './TicketCard';

export function TicketsContent({ condo, focusTicketId }) {
  const { condoSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { user, getToken, setTicketFollowState } = useAuth();
  const [allTickets, setAllTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('open');
  const highlightedTicketId = searchParams.get('ticket');
  const ticketRefs = useRef({});

  const handleFollowToggle = async (ticketId, isCurrentlyFollowing) => {
    const token = getToken();
    if (!token) return;
    const shouldFollow = !isCurrentlyFollowing;
    setTicketFollowState(ticketId, shouldFollow);
    try {
      if (isCurrentlyFollowing) {
        await axios.delete(`${getApiBaseUrl()}/api/tickets/${ticketId}/follow`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${getApiBaseUrl()}/api/tickets/${ticketId}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        track('ticket_followed');
      }
    } catch (err) {
      console.error('Failed to toggle follow:', err);
      setTicketFollowState(ticketId, isCurrentlyFollowing);
    }
  };

  const fetchTickets = useCallback(async (condoId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${getApiBaseUrl()}/api/tickets/condo/${condoId}`);
      setAllTickets(response.data.tickets || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      setError('Failed to load tickets. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (condo?._id) {
      fetchTickets(condo._id);
    }
  }, [condo, fetchTickets]);

  useEffect(() => {
    if (highlightedTicketId && allTickets.length > 0 && !loading) {
      const ticket = allTickets.find(t => t._id === highlightedTicketId);
      if (ticket) {
        if (ticket.status !== filter && filter !== 'all') {
          setFilter(ticket.status);
        }
        setTimeout(() => {
          ticketRefs.current[highlightedTicketId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [highlightedTicketId, allTickets, loading, filter]);

  const getLastUpdateTime = (ticket) => {
    if (ticket.updates && ticket.updates.length > 0) {
      const latestUpdate = [...ticket.updates].sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      )[0];
      return new Date(latestUpdate.timestamp);
    }
    return new Date(ticket.createdAt);
  };

  const getFilteredAndSortedTickets = () => {
    let filtered = allTickets;

    if (filter === 'open') {
      filtered = allTickets.filter(t => t.status === 'open');
    } else if (filter === 'closed') {
      filtered = allTickets.filter(t => t.status === 'closed');
    }

    if (filter === 'open') {
      const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
      return [...filtered].sort((a, b) => {
        const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return getLastUpdateTime(b) - getLastUpdateTime(a);
      });
    } else {
      return [...filtered].sort((a, b) => 
        getLastUpdateTime(b) - getLastUpdateTime(a)
      );
    }
  };

  const getFilteredCount = (status) => {
    if (status === 'open') {
      return allTickets.filter(t => t.status === 'open').length;
    } else if (status === 'closed') {
      return allTickets.filter(t => t.status === 'closed').length;
    }
    return allTickets.length;
  };

  // The focused ticket page (linked from notification emails) shows only the
  // ticket the reader was notified about, whatever the status filter would say.
  const focusTicket = focusTicketId ? allTickets.find(t => t._id === focusTicketId) : null;
  const displayTickets = focusTicketId ? (focusTicket ? [focusTicket] : []) : getFilteredAndSortedTickets();

  return (
    <div className="space-y-4">
      {focusTicketId ? (
        <Link
          to={`/${condoSlug}/tickets`}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          ← Back to tickets
        </Link>
      ) : (
      <div className="bg-card rounded-lg shadow-md p-1.5 border border-border sm:px-4 sm:py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="hidden items-center gap-2.5 sm:flex">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Ticket className="h-4 w-4" aria-hidden="true" />
            </span>
            <p className="text-base font-medium text-foreground">Ticket status</p>
          </div>
          <div role="group" aria-label="Filter by status" className="grid w-full grid-cols-2 gap-1.5 sm:w-auto sm:gap-1 sm:rounded-lg sm:bg-secondary/40 sm:p-1">
            <button
              onClick={() => setFilter('open')}
              className={`px-3 py-2 sm:px-5 sm:py-1.5 rounded-md text-sm font-semibold transition-colors ${
                filter === 'open'
                  ? 'bg-primary text-foreground shadow-md'
                  : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
              }`}
            >
              Open ({getFilteredCount('open')})
            </button>
            <button
              onClick={() => setFilter('closed')}
              className={`px-3 py-2 sm:px-5 sm:py-1.5 rounded-md text-sm font-semibold transition-colors ${
                filter === 'closed'
                  ? 'bg-muted-foreground/50 text-foreground shadow-md'
                  : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
              }`}
            >
              Closed ({getFilteredCount('closed')})
            </button>
          </div>
        </div>
      </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading tickets...</p>
        </div>
      )}

      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
          {error}
        </div>
      )}

      {!loading && !error && displayTickets.length === 0 && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          {focusTicketId ? (
            <>
              <p className="text-foreground text-lg">This ticket isn't available.</p>
              <p className="text-muted-foreground text-sm mt-2">
                It may have been removed, or it belongs to another residence.
              </p>
            </>
          ) : (
            <>
              <p className="text-foreground text-lg">No tickets found.</p>
              <p className="text-muted-foreground text-sm mt-2">
                Tickets will appear here when emails are processed.
              </p>
            </>
          )}
        </div>
      )}

      {!loading && !error && displayTickets.length > 0 && (
        <div className="space-y-4">
          {displayTickets.map((ticket) => (
            <TicketCard
              key={ticket._id}
              ticket={ticket}
              defaultExpanded={ticket._id === highlightedTicketId || ticket._id === focusTicketId}
              ticketRef={el => ticketRefs.current[ticket._id] = el}
              user={user}
              onFollowToggle={handleFollowToggle}
            />
          ))}
        </div>
      )}

      {focusTicketId && (
        <div className="text-center">
          <Link
            to={`/${condoSlug}/tickets`}
            className="inline-block px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors"
          >
            Go to all tickets
          </Link>
        </div>
      )}
    </div>
  );
}

function TicketsPage() {
  const { ticketId } = useParams();

  return (
    <CondoLayout>
      {({ condo }) => <TicketsContent condo={condo} focusTicketId={ticketId} />}
    </CondoLayout>
  );
}

export default TicketsPage;

